// react
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

// api
import { useGetLinkedEnergyGroupsLinkedEnergyGroupsAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';

// components
import useMatrikelList from '@/features/energyxplorer/portfolio/hooks/useMatrikelList';
import type { MatrikelItem } from '@/features/energyxplorer/types';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';

type EnergyType = 'power' | 'water' | 'heat';
type AnalysisField =
  | 'total_consumption_year'
  | 'current_calendar_year_sum'
  | 'total_consumption_last_year'
  | 'last_30_days_average';

const getTypeValue = (
  item: MatrikelItem,
  energyType: EnergyType,
  field: AnalysisField,
) => {
  const typeData = item[energyType];
  const analysis =
    (typeData?.matrikel_analysis as Record<string, unknown>) ?? {};
  const fromAnalysis = analysis[field] as number | undefined;
  if (fromAnalysis != null) return fromAnalysis;
  return typeData?.meter_list
    ?.filter((e) => e.hasAnalysis)
    .reduce((sum, e) => sum + ((e.analysis[field] ?? 0) as number), 0);
};

// A trend can't be summed across meters, so only matrikel-level analysis is used
const getMatrikelTrend = (item: MatrikelItem, energyType: EnergyType) =>
  (item[energyType]?.matrikel_analysis as Record<string, unknown> | undefined)
    ?.last_30_day_trend as number | null | undefined;

/**
 * One row per matrikel with all three energy types' totals merged and linked
 * energy groups attached. Shared by the portfolio analysis dashboard and the
 * portfolio overview grid.
 */
const usePortfolioAnalysisRows = () => {
  const account = useSelector(getSelectedAccount);
  const matrikelListResult = useMatrikelList();
  const { matrikel_list } = matrikelListResult;

  const { data: linkedEnergyGroupsData } =
    useGetLinkedEnergyGroupsLinkedEnergyGroupsAccountIdGetQuery(
      { accountId: account?.AccountId },
      { skip: !account?.AccountId },
    );

  const enrichedData = useMemo(() => {
    if (!matrikel_list?.length) return null;

    const linkedEnergyGroups = linkedEnergyGroupsData?.LinkedEnergyGroups || [];

    const list = matrikel_list
      .filter((item) =>
        (['power', 'water', 'heat'] as const).some(
          (et) =>
            (item[et].meter_list?.length ?? 0) > 0 ||
            Object.keys(
              (item[et].matrikel_analysis as Record<string, unknown>) ?? {},
            ).length > 0,
        ),
      )
      .map((item) => {
        const { metadata } = item;
        // Power analysis supplies common fields (occupancy, etc.) with heat as fallback
        const primaryAnalysis =
          (item.power.matrikel_analysis as Record<string, unknown>) ??
          (item.heat.matrikel_analysis as Record<string, unknown>) ??
          {};

        return {
          ...primaryAnalysis,
          matrikel_key: metadata.MatrikelKey,
          display_name: metadata.Address || metadata.MainAddress,
          main_address: metadata.Address || metadata.MainAddress,
          type: 'matrikel',
          power_total_consumption_year: getTypeValue(
            item,
            'power',
            'total_consumption_year',
          ),
          power_total_consumption_last_year: getTypeValue(
            item,
            'power',
            'total_consumption_last_year',
          ),
          heat_total_consumption_year: getTypeValue(
            item,
            'heat',
            'total_consumption_year',
          ),
          heat_total_consumption_last_year: getTypeValue(
            item,
            'heat',
            'total_consumption_last_year',
          ),
          water_total_consumption_year: getTypeValue(
            item,
            'water',
            'total_consumption_year',
          ),
          water_total_consumption_last_year: getTypeValue(
            item,
            'water',
            'total_consumption_last_year',
          ),
          power_current_calendar_year_sum: getTypeValue(
            item,
            'power',
            'current_calendar_year_sum',
          ),
          heat_current_calendar_year_sum: getTypeValue(
            item,
            'heat',
            'current_calendar_year_sum',
          ),
          water_current_calendar_year_sum: getTypeValue(
            item,
            'water',
            'current_calendar_year_sum',
          ),
          power_last_30_days_average: getTypeValue(
            item,
            'power',
            'last_30_days_average',
          ),
          heat_last_30_days_average: getTypeValue(
            item,
            'heat',
            'last_30_days_average',
          ),
          water_last_30_days_average: getTypeValue(
            item,
            'water',
            'last_30_days_average',
          ),
          power_last_30_day_trend: getMatrikelTrend(item, 'power'),
          heat_last_30_day_trend: getMatrikelTrend(item, 'heat'),
          water_last_30_day_trend: getMatrikelTrend(item, 'water'),
          SumBuildingArea: metadata.OverwriteFlag
            ? (metadata.OverwriteSumBuildingArea ?? metadata.SumBuildingArea)
            : metadata.SumBuildingArea,
          ExtractedBuildingUse: metadata.OverwriteFlag
            ? (metadata.OverwriteExtractedBuildingUse ??
              metadata.ExtractedBuildingUse)
            : metadata.ExtractedBuildingUse,
          linked_energy_groups: linkedEnergyGroups
            .find((leg) => leg.MatrikelKey === metadata.MatrikelKey)
            ?.LinkedEnergyGroups.filter(
              (group) => !group.type?.toLowerCase().includes('portfolio'),
            ),
        } as Record<string, unknown>;
      });

    if (!list.length) return null;
    return { portfolio_analysis_list: list };
  }, [matrikel_list, linkedEnergyGroupsData]);

  return { ...matrikelListResult, enrichedData };
};

export default usePortfolioAnalysisRows;
